/**
 * CAOS-499 — the seam `<caos-difference-modal>` holds its comparison engine behind.
 *
 * Same argument as `code-editor-engine.ts`, and for the same clause of the specification: the
 * difference engine is an implementation detail behind the component's own contract. A library that
 * hard-wired one comparison algorithm would make swapping it a change to every screen that shows a
 * difference, which is exactly backwards — the screens are the promise and the algorithm is not.
 *
 * WHAT THE COMPONENT NEEDS, and therefore all this seam carries: an ordered list of rows, each
 * saying what happened to one line. The component draws rows; it does not know how they were
 * decided. An engine that produces word-level or syntax-aware differences still produces rows here,
 * and one that produces nothing better than "these two files differ" produces two rows.
 *
 * THE BUILT-IN ENGINE is a longest-common-subsequence line diff. That is deliberately the plain,
 * well-understood answer rather than a fast approximation: the surface it serves compares metadata
 * between two environments, where a difference reported in the wrong place is worse than a
 * comparison that took another few milliseconds.
 */

/** What happened to one line. */
export type DiffOp = 'equal' | 'added' | 'removed';

/**
 * One row of a comparison.
 *
 * Both sides carry their own line number, and both are OPTIONAL rather than defaulted to zero,
 * because a line that exists on only one side genuinely has no number on the other. Writing 0 there
 * would put a line number in a column that has no line, which is the sort of small lie a person
 * reading a difference notices and then stops trusting the whole screen over.
 */
export interface DiffRow {
  readonly op: DiffOp;
  /** The line as it reads on the source side. Absent ⇒ it is not there. */
  readonly left?: string;
  /** The line as it reads on the target side. Absent ⇒ it is not there. */
  readonly right?: string;
  /** Its 1-based line number on the source side, when it has one. */
  readonly leftLine?: number;
  /** Its 1-based line number on the target side, when it has one. */
  readonly rightLine?: number;
}

/** Something that can compare two texts. */
export interface DiffEngine {
  /** A name, so a component can say which engine drew the rows and a test can assert the swap took. */
  readonly name: string;
  /** Compare, source first. */
  diff(left: string, right: string): readonly DiffRow[];
}

/** Split into lines without inventing a trailing empty one for a text that ends in a newline. */
function lines(text: string): string[] {
  if (text === '') return [];
  const out = text.replace(/\r\n/g, '\n').split('\n');
  if (out.length > 0 && out[out.length - 1] === '') out.pop();
  return out;
}

/**
 * The engine `@caos/ui` ships with: a longest-common-subsequence line comparison.
 *
 * Lines that both sides share, in order, are the common subsequence; everything else is a removal
 * from the source or an addition on the target. Removals are emitted BEFORE the additions they sit
 * beside, so a changed line reads as the old one struck through above the new one — which is how a
 * person reads a change rather than two unrelated events.
 */
export const LINE_DIFF_ENGINE: DiffEngine = {
  name: 'line-lcs',
  diff(leftText: string, rightText: string): readonly DiffRow[] {
    const a = lines(leftText);
    const b = lines(rightText);

    // table[i][j] = length of the longest common subsequence of a[i…] and b[j…].
    const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i -= 1) {
      for (let j = b.length - 1; j >= 0; j -= 1) {
        table[i]![j] = a[i] === b[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
      }
    }

    const rows: DiffRow[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) {
        rows.push({ op: 'equal', left: a[i]!, right: b[j]!, leftLine: i + 1, rightLine: j + 1 });
        i += 1;
        j += 1;
      } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
        rows.push({ op: 'removed', left: a[i]!, leftLine: i + 1 });
        i += 1;
      } else {
        rows.push({ op: 'added', right: b[j]!, rightLine: j + 1 });
        j += 1;
      }
    }
    while (i < a.length) {
      rows.push({ op: 'removed', left: a[i]!, leftLine: i + 1 });
      i += 1;
    }
    while (j < b.length) {
      rows.push({ op: 'added', right: b[j]!, rightLine: j + 1 });
      j += 1;
    }
    return rows;
  },
};

let installed: DiffEngine = LINE_DIFF_ENGINE;

/** Install the comparison engine every difference surface in this library will use from here on. */
export function setDiffEngine(engine: DiffEngine | null): void {
  installed = engine ?? LINE_DIFF_ENGINE;
}

/** The engine a component should compare with. */
export function diffEngine(): DiffEngine {
  return installed;
}

/**
 * How many rows of each kind — the count a difference surface states above the rows.
 *
 * Here rather than in the component because the compare view states the same figures without opening
 * the modal, and two counts of the same thing computed in two places is how a summary comes to
 * disagree with what it summarises.
 */
export function diffTally(rows: readonly DiffRow[]): { added: number; removed: number; equal: number } {
  let added = 0;
  let removed = 0;
  let equal = 0;
  for (const row of rows) {
    if (row.op === 'added') added += 1;
    else if (row.op === 'removed') removed += 1;
    else equal += 1;
  }
  return { added, removed, equal };
}
